// invest_FE/src/components/property/display/PropertyActions.js
import React, { useContext, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FaHeart, FaRegHeart, FaEnvelope } from 'react-icons/fa';
import { useLanguage } from '../../../hooks/useLanguage';
import { UserContext } from '../../../context/UserContext';
import { NotificationContext } from '../../../context/NotificationContext';
import { apiFetch } from '../../../utils/apiClient';
import { BASE_URL } from '../../../utils/config';
import InquiryMessageModal from '../../modals/InquiryMessageModal';
import InvestmentProfileModal from '../../modals/InvestmentProfileModal';
import { analyzeInquiryMatch } from '../../../utils/inquiryMatcher';
import { Role } from '../../../utils/enums';
import './PropertyActions.css';

const PropertyActions = ({ property, onStatsUpdate }) => {
  const { user, updateUser } = useContext(UserContext);
  const { addNotification } = useContext(NotificationContext);
  const navigate = useNavigate();
  const lang = useLanguage();
  const { t } = useTranslation();

  const [isInquiryModalOpen, setIsInquiryModalOpen] = useState(false);
  const [isSendingInquiry, setIsSendingInquiry] = useState(false);
  const [matchAnalysisResult, setMatchAnalysisResult] = useState(null);
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);
  const [profileModalMode, setProfileModalMode] = useState('fill');
  const [initialProfileData, setInitialProfileData] = useState({});

  const isSaved = user && user.isLoggedIn && Array.isArray(user.savedProperties) && user.savedProperties.includes(property?.propertyID);

  const handleAuthRedirect = (path) => {
    if (property && property.propertyID) {
      localStorage.setItem('returnToPropertyId', property.propertyID);
    }
    navigate(path);
  };

  const handleSaveToggle = async () => {
    if (!user || !user.isLoggedIn) {
      addNotification(t('propertyActions.loginToSave'), 'info', 0, true, [
        { text: t('propertyActions.login'), onClick: () => handleAuthRedirect(`/${lang}/login`) },
        { text: t('propertyActions.register'), onClick: () => handleAuthRedirect(`/${lang}/subscribe`) },
        { text: t('propertyActions.close'), onClick: () => {} },
      ]);
      return;
    }

    if (user.username === property.sellerUsername) {
      addNotification(t('propertyActions.cannotSaveOwn'), 'warning');
      return;
    }

    const value = isSaved ? -1 : 1;

    try {
      const url = `${BASE_URL}/api/properties/property/${property.propertyID}/stats`;
      const options = {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ saveCount: value }),
      };

      const response = await apiFetch(url, options, user.token);
      const updatedData = await response.json();

      onStatsUpdate(updatedData.stats);

      if (updatedData.savedProperties) {
        updateUser({ savedProperties: updatedData.savedProperties });
      }
    } catch (error) {
      console.error(`Failed to ${isSaved ? 'unsave' : 'save'} property:`, error);
      addNotification(t('propertyActions.error', { message: error.message }), 'error');
    }
  };

  const handleSendInquiry = async (message) => {
    setIsSendingInquiry(true);
    try {
      const response = await apiFetch(
        `${BASE_URL}/api/inquiries`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            propertyId: property.propertyID,
            message,
            profileConfirmed: true, // Assuming profile is confirmed at this stage
            matchAnalysis: matchAnalysisResult,
          }),
        },
        user.token
      );

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || t('propertyActions.inquirySendFailed'));
      }

      addNotification(t('propertyActions.inquirySent'), 'success');
      if (onStatsUpdate) {
        onStatsUpdate(data.stats); // Update stats with the new inquiry count
      }
      setIsInquiryModalOpen(false);
    } catch (error) {
      addNotification(t('propertyActions.error', { message: error.message }), 'error');
    } finally {
      setIsSendingInquiry(false);
    }
  };

  /**
   * Checks if a user's investment profile is complete.
   * @param {object} profile - The user's investmentProfile sub-document.
   * @returns {boolean} - True if the profile is complete, false otherwise.
   */
  const isInvestmentProfileComplete = (profile) => {
    if (!profile) return false;
    const requiredFields = ['investmentStrategy', 'financingMethod', 'investmentBudget', 'experienceLevel', 'purchaseTimeline', 'desiredInvolvement'];
    return requiredFields.every((field) => {
      const value = profile[field];
      if (value == null) return false;
      if (Array.isArray(value)) {
        return value.length > 0;
      }
      return value !== '';
    });
  };

  const runInquiryChecks = async (profile) => {
    if (!user || !user.isLoggedIn || !profile) {
      addNotification(t('propertyActions.loginToContact'), 'info', 0, true, [
        { text: t('propertyActions.login'), onClick: () => handleAuthRedirect(`/${lang}/login`) },
        { text: t('propertyActions.register'), onClick: () => handleAuthRedirect(`/${lang}/subscribe`) },
        { text: t('propertyActions.close'), onClick: () => {} },
      ]);
      return;
    }

    const analysis = analyzeInquiryMatch({ ...user, investmentProfile: profile }, property);

    // Step 1: Hard filter
    if (analysis.mismatchCount >= 2) {
      const message = t('propertyActions.mismatchMessage');
      addNotification(message, 'info', 0, true, [
        {
          text: t('propertyActions.updateProfile'),
          onClick: () => {
            setInitialProfileData(user.investmentProfile || {});
            setProfileModalMode('fill');
            setIsProfileModalOpen(true);
          },
        },
        { text: t('propertyActions.close'), onClick: () => {} },
      ]);
      return;
    }

    // Step 2: Budget check (if needed)
    if (!analysis.budget) {
      const canAfford = await new Promise((resolve) => {
        addNotification(t('propertyActions.budgetWarning'), 'warning', 0, true, [
          { text: t('propertyActions.yesCanAfford'), onClick: () => resolve(true) },
          { text: t('propertyActions.no'), onClick: () => resolve(false) },
        ]);
      });
      if (!canAfford) return;
      analysis.budget = true; // User confirmed they can afford it
    }

    // Step 3: All-cash check (if needed)
    if (!analysis.allCash) {
      const canPayCash = await new Promise((resolve) => {
        addNotification(t('propertyActions.allCashWarning'), 'warning', 0, true, [
          { text: t('propertyActions.yes'), onClick: () => resolve(true) },
          { text: t('propertyActions.no'), onClick: () => resolve(false) },
        ]);
      });
      if (!canPayCash) return;
      analysis.allCash = true; // User confirmed they can pay cash
    }

    // If all checks pass, open the message modal
    const { mismatchCount, ...analysisForBackend } = analysis;
    setMatchAnalysisResult(analysisForBackend);
    setIsInquiryModalOpen(true);
  };

  const handleInitiateInquiry = () => {
    // --- DIAGNOSTIC LOG ---
    console.log('User object inside handleInitiateInquiry:', user);
    console.log('Role from context:', user.role);
    console.log('Role enum UserPendingVerification:', Role.UserPendingVerification);
    console.log('Condition check (user.role === Role.UserPendingVerification):', user.role === Role.UserPendingVerification);
    // --- END DIAGNOSTIC LOG ---

    if (!user || !user.isLoggedIn) {
      addNotification(t('propertyActions.loginToContact'), 'info', 0, true, [
        { text: t('propertyActions.login'), onClick: () => handleAuthRedirect(`/${lang}/login`) },
        { text: t('propertyActions.register'), onClick: () => handleAuthRedirect(`/${lang}/subscribe`) },
        { text: t('propertyActions.close'), onClick: () => {} },
      ]);
      return;
    }

    if (user.role === Role.UserPendingVerification || user.role === Role.SellerPendingVerification) {
      addNotification(t('propertyActions.completeVerification'), 'warning', 0, true, [
        {
          text: t('propertyActions.goToMyAccount'),
          onClick: () => navigate(`/${lang}/preferences`),
        },
        { text: t('propertyActions.close'), onClick: () => {} },
      ]);
      return;
    }

    if (user.username === property.sellerUsername) {
      addNotification(t('propertyActions.cannotInquireOwn'), 'warning');
      return;
    }

    if (isInvestmentProfileComplete(user.investmentProfile)) {
      runInquiryChecks(user.investmentProfile);
    } else {
      // If profile is incomplete, open the modal to have the user fill it out.
      setInitialProfileData(user.investmentProfile || {});
      setProfileModalMode('fill');
      setIsProfileModalOpen(true);
    }
  };

  const handleProfileSave = (updatedProfile) => {
    runInquiryChecks(updatedProfile);
  };

  const handleEditPreferences = () => {
    setIsInquiryModalOpen(false);
    navigate(`/${lang}/preferences`);
  };

  const isOwner = user && user.isLoggedIn && user.username === property.sellerUsername;

  return (
    <>
      <div className="property-actions">
        <button onClick={handleSaveToggle} className={`stat-button save-button ${isSaved ? 'saved' : ''}`}>
          {isSaved ? <FaHeart /> : <FaRegHeart />} {isSaved ? t('propertyActions.saved') : t('propertyActions.saveProperty')}
        </button>
        {!isOwner && (
          <button onClick={handleInitiateInquiry} className="stat-button contact-button" disabled={isSendingInquiry}>
            <FaEnvelope /> {isSendingInquiry ? t('propertyActions.processing') : t('propertyActions.contactSeller')}
          </button>
        )}
      </div>

      <InquiryMessageModal isOpen={isInquiryModalOpen} onClose={() => setIsInquiryModalOpen(false)} onSend={handleSendInquiry} isLoading={isSendingInquiry} onEditPreferences={handleEditPreferences} />

      <InvestmentProfileModal isOpen={isProfileModalOpen} onClose={() => setIsProfileModalOpen(false)} initialProfileData={initialProfileData} onSave={handleProfileSave} mode={profileModalMode} />
    </>
  );
};

export default PropertyActions;
